import * as Sentry from '@sentry/react';

/**
 * Session Replay stays off while the Law Library is on screen (#1964; Plan 3 Decision 18, Plan 5 Decision 10).
 *
 * rrweb takes a full snapshot whenever an incremental event arrives more than `checkoutEveryNms`
 * after the last one (60 s in buffer mode, @sentry-internal/replay 10.42.0), and every full
 * snapshot starts with a Meta event holding window.location.href. No Sentry hook sees that event
 * (beforeAddRecordingEvent is for custom events only), and the page's URL holds the query. Stopping
 * Replay from the route's effect left a gap: after pushState('/law-library?q=…') the lazy route's
 * own DOM changes could trigger a checkout before the effect ran. The guard closes it: a history
 * change whose destination is the Law Library stops Replay synchronously BEFORE the URL changes.
 *
 * Restarting takes a full snapshot of the URL of that moment, so Replay is restarted only when
 * BOTH no gate holds the pause AND the URL is not the Law Library's. A gate that unmounts while
 * the URL is still /law-library (an error boundary) leaves Replay stopped; the guard restarts it
 * after the next move away. Either way it restarts in the mode it was found in.
 */

type ReplayMode = 'session' | 'buffer';
type HistoryMethod = History['pushState'];

const LIBRARY_PATH = '/law-library';

let pausedMode: ReplayMode | null = null;
let holders = 0;

export function isReplayPausedPath(pathname: string): boolean {
  return pathname === LIBRARY_PATH || pathname.startsWith(`${LIBRARY_PATH}/`);
}

/** Stop a running Replay, remembering its mode. Nothing when none runs or it is already paused. */
export function pauseReplay(): void {
  if (pausedMode) return;
  const replay = Sentry.getReplay();
  const mode = replay?.getReplayId() ? replay.getRecordingMode() : null;
  if (!replay || !mode) return;
  pausedMode = mode;
  // stop() stops recording synchronously before its first await (in session mode it then flushes; the recording has already stopped).
  void replay.stop();
}

/**
 * Restart Replay in the mode pauseReplay found it in — only when it was paused, no gate holds
 * the pause, and `pathname` (the URL now on screen) is not the Law Library's.
 */
function resumeIfAllowed(pathname: string): void {
  if (!pausedMode || holders > 0 || isReplayPausedPath(pathname)) return;
  const mode = pausedMode;
  pausedMode = null;
  const replay = Sentry.getReplay();
  if (!replay) return;
  if (mode === 'buffer') replay.startBuffering();
  else replay.start();
}

function currentPathname(): string {
  return typeof window === 'undefined' ? LIBRARY_PATH : window.location.pathname;
}

/** The gate's hold: pause now; the returned release resumes only by resumeIfAllowed's rule. */
export function holdReplayPause(): () => void {
  holders += 1;
  pauseReplay();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders -= 1;
    resumeIfAllowed(currentPathname());
  };
}

/** Tests only: forget any pause and holder. */
export function resetReplayPause(): void {
  pausedMode = null;
  holders = 0;
}

function pathnameOf(url: string | URL | null | undefined, base: string): string | null {
  if (url === null || url === undefined) return null;
  try {
    return new URL(String(url), base).pathname;
  } catch {
    return null;
  }
}

/**
 * Patch history.pushState / replaceState and listen for popstate, so a move to the Law Library
 * stops Replay before the route renders, and a move away (with no gate holding the pause)
 * restarts it after the URL has changed. Call once, after Sentry.init; returns an uninstaller.
 */
export function installReplayRouteGuard(win: Window): () => void {
  const { history } = win;
  const originalPush = history.pushState;
  const originalReplace = history.replaceState;
  const guard = (original: HistoryMethod): HistoryMethod => function guarded(this: History, data: unknown, unused: string, url?: string | URL | null) {
    const destination = pathnameOf(url, win.location.href);
    if (destination && isReplayPausedPath(destination)) pauseReplay();
    original.call(this, data, unused, url);
    resumeIfAllowed(win.location.pathname);
  };
  const onPopState = () => {
    if (isReplayPausedPath(win.location.pathname)) pauseReplay();
    else resumeIfAllowed(win.location.pathname);
  };
  history.pushState = guard(originalPush);
  history.replaceState = guard(originalReplace);
  win.addEventListener('popstate', onPopState);
  if (isReplayPausedPath(win.location.pathname)) pauseReplay();
  return () => {
    history.pushState = originalPush;
    history.replaceState = originalReplace;
    win.removeEventListener('popstate', onPopState);
  };
}
